import React, { useEffect, useState } from 'react'
import useListCompany from '../../../../../hooks/useListCompany';
import { getFetcher, getPdfUrl } from '../../../../../services/api';
import useSWR from 'swr';
import { Button, Card, CardBody, CardHeader, Divider, Spinner, TableCell, TableRow } from '@nextui-org/react';
import ModalFinalizeReport from './ModalFinalizeReport';
import { format } from 'date-fns';
import ModalReportReview from './ModalReportReview';
import FilterReactSelect from '../../../../elements/FilterReactSelect';
import ClientTablePagination from '../../../../elements/ClientTablePagination';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import useAuth from '../../../../../hooks/useAuth';
import RolesAccess from '../../../../../enums/roles';
import { formattedDate, hasValidRole, hasValidStatus } from '../../../../../services/helpers';
import { useSelectedCompany } from '../../../../../hooks/useSelectedCompany';
import StatusPengajuanPelaporan from '../../../../../enums/statusPengajuanPelaporan';

/**
 * PILIHAN PERUSAHAAN & PERIODE BISA DIKENDALIKAN INDUK
 *
 * Saat dipakai sebagai tab di halaman pelaporan (Pembelian, Penggunaan),
 * induknya mengirim state pilihan beserta setter-nya sehingga tab Periode Aktif,
 * Preview, dan Riwayat memakai satu pilihan yang sama - siapa pun yang memilih
 * lebih dulu, tab lain ikut. Tanpa prop itu komponen ini tetap berdiri sendiri
 * dengan state internalnya, seperti pada halaman Preview Laporan tersendiri.
 */
export default function TableReportPreview({
    isPreviewOnly = false,
    selectedCompany: companyInduk,
    setSelectedCompany: setCompanyInduk,
    selectedPeriod: periodeInduk,
    setSelectedPeriod: setPeriodeInduk,
}) {
    const {roles} = useAuth();
    const [companySendiri, setCompanySendiri] = useSelectedCompany()
    const [periodeSendiri, setPeriodeSendiri] = useState(null);

    const dikendalikanInduk = typeof setPeriodeInduk === 'function';
    const selectedCompany = dikendalikanInduk ? companyInduk : companySendiri;
    const setSelectedCompany = typeof setCompanyInduk === 'function' ? setCompanyInduk : setCompanySendiri;
    const selectedPeriod = dikendalikanInduk ? periodeInduk : periodeSendiri;
    const setSelectedPeriod = dikendalikanInduk ? setPeriodeInduk : setPeriodeSendiri;
    const { data, isLoading, mutate } = useSWR(
        selectedCompany && selectedPeriod
            ? `/api/period/preview-report?companyId=${selectedCompany.value}&periodId=${selectedPeriod.value}`
            : null, 
        getFetcher
    )
    const { dataCompany, isLoadingCompany } = useListCompany()
    const { data: dataPeriod, isLoading: isLoadingPeriod } = useSWR(`/api/period/all`, getFetcher);

    // Periode aktif hanya diisikan bila BELUM ada pilihan. Dulu efek ini selalu
    // menimpa, sehingga pilihan periode dari tab sebelah langsung terganti
    // begitu tab Preview dibuka.
    useEffect(() => {
        if (selectedPeriod || !dataPeriod) return;
        const activePeriod = dataPeriod.find((item) => item?.isActive === true);
        if (activePeriod) {
            setSelectedPeriod({ label: activePeriod.name, value: activePeriod.id });
        }
    }, [dataPeriod, selectedPeriod, setSelectedPeriod]);

    useEffect(() => {
        if(data?.message){
            toast.error(data?.message)
        }
    })

    // Fungsi format angka pintar
    const formatNumber = (num) => {
        // 1. Cek apakah num kosong atau bukan angka, kembalikan "0"
        if (num === null || num === undefined || isNaN(num)) return "0";

        // 2. Format menggunakan standar angka Indonesia (id-ID)
        return new Intl.NumberFormat("id-ID", {
        minimumFractionDigits: 0, // Jangan paksa ada angka di belakang koma jika angkanya bulat
        maximumFractionDigits: 2  // Maksimal 2 angka di belakang koma jika angkanya desimal
        }).format(num);
    };
    const header = ['Jenis B3', 'Stok Awal', 'Pembelian Lokal', 'Pembelian Impor', 'Penggunaan', 'Distribusi', 'Produksi', 'Stok Akhir'];
    const content = (item) => (
        <TableRow key={item.id}>
            <TableCell>
                {item.dataBahanB3.casNumber} - {item.dataBahanB3.namaBahanKimia}
            </TableCell>
            <TableCell className="text-ink-500 text-right">{formatNumber(item.stokAwal)} Kg</TableCell>
            <TableCell className="text-success text-right">+ {formatNumber(item.pembelianLokal)} Kg</TableCell>
            <TableCell className="text-success text-right">+ {formatNumber(item.pembelianImport)} Kg</TableCell>
            <TableCell className="text-danger text-right">- {formatNumber(item.penggunaan)} Kg</TableCell>
            <TableCell className="text-danger text-right">- {formatNumber(item.distribusi)} Kg</TableCell>
            <TableCell className="text-success text-right">+ {formatNumber(item.produksi)} Kg</TableCell>
            <TableCell className="text-ink-500 text-right">{formatNumber(item.stokAkhir)} Kg</TableCell>
        </TableRow>
    );

    async function openPdfDirectly(endpoint) {
        try {
            await getPdfUrl(endpoint)
        } catch (error) {            
            toast.error('Failed to open PDF');
        }
    };

    return (
        <>
            {
                !isPreviewOnly && 
                <div className='flex items-center justify-end gap-2'>
                    {hasValidRole(roles, [RolesAccess.SUPER_ADMIN, RolesAccess.PENGELOLA]) && hasValidStatus(data?.statusPelaporan, [StatusPengajuanPelaporan.DRAFT, null, undefined]) && (
                        <ModalFinalizeReport 
                            endpoint="/api/period/finalize-pelaporan" 
                            mutate={mutate} 
                            selectedCompany={selectedCompany}
                            selectedPeriod={selectedPeriod}
                            setSelectedCompany={setSelectedCompany} 
                            setSelectedPeriod={setSelectedPeriod}
                        />
                    )}
                    {hasValidRole(roles, [RolesAccess.SUPER_ADMIN, RolesAccess.PIC_PELAPORAN]) && hasValidStatus(data?.statusPelaporan, [StatusPengajuanPelaporan.MENUNGGU_PERSETUJUAN]) && (
                        <ModalReportReview endpoint="/api/period/review-and-approve" /> 
                    )}
                </div>
            }
            {
                isLoading
                ?
                <div className="flex items-center justify-center mt-3">
                    <Spinner/>
                </div>
                :
                <div className='grid grid-cols-4 gap-3 mt-3'>
                    <div className='col-span-1 flex flex-col gap-3'>
                        <Card radius='sm' className='col-span-1'>
                            <CardHeader>Perusahaan</CardHeader>
                            <Divider/>
                            <CardBody className='flex flex-col gap-3'>
                                <div className='flex flex-col'>
                                    <span className='text-xs text-ink-400 uppercase'>Nama Perusahaan</span>
                                    <span className='text-sm font-medium'>{data?.company?.name || '-'}</span>
                                </div>
                                <div className='flex flex-col'>
                                    <span className='text-xs text-ink-400 uppercase'>Alamat</span>
                                    <span className='text-sm font-medium'>{data?.company?.alamatKantor || '-'}</span>
                                </div>
                                <div className='flex flex-col'>
                                    <span className='text-xs text-ink-400 uppercase'>Tipe Perusahaan</span>
                                    <span className='text-sm font-medium'>{data?.company?.tipePerusahaan?.map((item) => item?.name).join(', ') || '-'}</span>
                                </div>
                                <div className='flex flex-col'>
                                    <span className='text-xs text-ink-400 uppercase'>Email Perusahaan</span>
                                    <span className='text-sm font-medium'>{data?.company?.emailKantor || '-'}</span>
                                </div>
                                <div className='flex flex-col'>
                                    <span className='text-xs text-ink-400 uppercase'>Fax Perusahaan</span>
                                    <span className='text-sm font-medium'>{data?.company?.faxKantor || '-'}</span>
                                </div>
                            </CardBody>
                        </Card>
                        <Card radius='sm' className='col-span-1'>
                            <CardHeader>Periode</CardHeader>
                            <Divider/>
                            <CardBody className='flex flex-col gap-3'>
                                <div className='flex flex-col'>
                                    <span className='text-xs text-ink-400 uppercase'>Periode</span>
                                    <span className='text-sm font-medium'>{data?.period?.name || '-'}</span>
                                </div>
                                <div className='flex flex-col'>
                                    <span className='text-xs text-ink-400 uppercase'>Tanggal Mulai Periode</span>
                                    <span className='text-sm font-medium'>{data?.period?.startPeriodDate ? formattedDate(data?.period?.startPeriodDate): '-'}</span>
                                </div>
                                <div className='flex flex-col'>
                                    <span className='text-xs text-ink-400 uppercase'>Tanggal Akhir Periode</span>
                                    <span className='text-sm font-medium'>{data?.period?.endPeriodDate ? formattedDate(data?.period?.endPeriodDate) : '-'}</span>
                                </div>
                                <div className='flex flex-col'>
                                    <span className='text-xs text-ink-400 uppercase'>Tanggal Akhir Finalisasi</span>
                                    <span className='text-sm font-medium'>{data?.period?.finalizationDeadline ? formattedDate(data?.period?.finalizationDeadline) : '-'}</span>
                                </div>
                            </CardBody>
                        </Card>
                    </div>
                    <Card radius='sm' className='col-span-3'>
                        <CardHeader>Preview Laporan</CardHeader>
                        <Divider/>
                        <CardBody>
                            <div className="w-full flex justify-end items-center gap-2 mb-3">
                                <div className="w-2/4 flex flex-col justify-end">
                                    <div className='flex items-center gap-2'>
                                        {selectedCompany && selectedPeriod && hasValidStatus(data?.statusPelaporan, [StatusPengajuanPelaporan.DRAFT, StatusPengajuanPelaporan.MENUNGGU_PERSETUJUAN, null, undefined])
                                            ? <Button 
                                                size="sm" 
                                                color='primary' 
                                                onPress={() => openPdfDirectly(`/api/pdf/generateBuktiLapor/${selectedCompany.value}/${selectedPeriod.value}`)} 
                                                startContent={<ArrowDownTrayIcon className="size-4" />}
                                            >
                                                Bukti Laporan
                                            </Button>
                                            : ''
                                        }
                                        {selectedCompany && selectedPeriod && data?.statusPelaporan === StatusPengajuanPelaporan.DISETUJUI
                                            ? <Button 
                                                size="sm" 
                                                color='primary' 
                                                startContent={<ArrowDownTrayIcon className="size-4" />}
                                                onPress={() => openPdfDirectly(`/api/pdf/generateTandaTerima/${selectedCompany.value}/${selectedPeriod.value}`)}
                                            >
                                                Tanda Terima
                                            </Button>
                                            : ''
                                        }
                                    </div>
                                </div>
                                <div className="w-1/4">
                                    <FilterReactSelect
                                        defaultValue={selectedCompany}
                                        placeholder="Cari Perusahaan"
                                        options={dataCompany?.data?.map(item => ({label: item.name, value: item.id}))}
                                        isLoading={isLoadingCompany}
                                        setValue={setSelectedCompany}
                                    />
                                </div>
                                <div className="w-1/4">
                                    <FilterReactSelect
                                        defaultValue={selectedPeriod}
                                        placeholder="Cari Periode"
                                        options={dataPeriod?.map(item => ({label: item.name, value: item.id}))}
                                        isLoading={isLoadingPeriod}
                                        setValue={setSelectedPeriod}
                                    />
                                </div>
                            </div>
                            <ClientTablePagination header={header} content={content} isLoading={isLoading} data={data?.preview} />
                        </CardBody>
                    </Card>
                </div>
            }
        </>
    )
}
